// src/pages/sow-template/SectionsPanel.jsx
//
// Template metadata + the section list (reorder/delete) + the
// placeholders palette. Split out of SowTemplatePage.jsx. Deliberately
// keeps "click a chip to insert it" (muted chips) visually distinct from
// "click a chip to register it" (the unregistered-placeholders warning,
// amber) -- in the old page these looked like the same kind of control.

import PropTypes from "prop-types";
import { ChevronDown, ChevronUp, Plus, Tag, X } from "lucide-react";
import { Alert, Button, FormField, Input, SurfaceCard } from "../../shared/ui";

export default function SectionsPanel({
  tpl,
  onFieldChange,
  sections,
  selectedSectionKey,
  onSelectSection,
  onMoveSection,
  onDeleteSection,
  onAddSection,
  unregisteredPlaceholders,
  onRegisterPlaceholder,
  allPlaceholders,
  onInsertPlaceholder,
  onOpenAddPlaceholder,
}) {
  return (
    <SurfaceCard className="mb-4">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h2 className="text-base font-semibold text-ink">Sections{tpl ? ` — ${tpl.name}` : ""}</h2>
        {tpl && (
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={onOpenAddPlaceholder}>
              <Tag size={14} /> Placeholder
            </Button>
            <Button size="sm" onClick={onAddSection}>
              <Plus size={14} /> Add Section
            </Button>
          </div>
        )}
      </div>

      {!tpl ? (
        <p className="text-sm text-muted">Select or create a template above.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <FormField label="Template Name">
              <Input value={tpl.name} onChange={(e) => onFieldChange("name", e.target.value)} />
            </FormField>
            <FormField label="Version">
              <Input value={tpl.version} onChange={(e) => onFieldChange("version", e.target.value)} />
            </FormField>
            <FormField label="Contract Type" helperText="Optional — e.g. T&M, Fixed Bid">
              <Input value={tpl.contract_type} onChange={(e) => onFieldChange("contract_type", e.target.value)} />
            </FormField>
          </div>

          {unregisteredPlaceholders.length > 0 && (
            <Alert variant="warning" title="Unregistered placeholders" className="mb-4">
              These are used in your content but not yet registered — click one to register it.
              <div className="flex gap-2 flex-wrap mt-2">
                {unregisteredPlaceholders.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => onRegisterPlaceholder(k)}
                    className="text-xs font-mono px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200"
                  >
                    {k}
                  </button>
                ))}
              </div>
            </Alert>
          )}

          <div className="flex gap-2 flex-wrap mb-4">
            {sections.map((s) => {
              const isActive = selectedSectionKey === s.key;
              return (
                <div
                  key={s.key}
                  onClick={() => onSelectSection(s.key)}
                  className={`flex items-center gap-1 rounded-lg border pl-3 pr-1.5 py-1.5 cursor-pointer transition-colors ${
                    isActive ? "border-brand-600 bg-brand-50" : "border-line hover:bg-gray-50"
                  }`}
                >
                  <span className={`text-sm ${isActive ? "text-brand-600 font-medium" : "text-ink"}`}>{s.title}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveSection(s.key, -1);
                    }}
                    className="p-1 text-muted hover:text-ink"
                    title="Move up"
                  >
                    <ChevronUp size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveSection(s.key, 1);
                    }}
                    className="p-1 text-muted hover:text-ink"
                    title="Move down"
                  >
                    <ChevronDown size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteSection(s.key);
                    }}
                    className="p-1 text-red-400 hover:text-red-600"
                    title="Delete section"
                  >
                    <X size={13} />
                  </button>
                </div>
              );
            })}
            {sections.length === 0 && <p className="text-sm text-muted">No sections yet — add one to get started.</p>}
          </div>

          {allPlaceholders.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted">Insert placeholder:</span>
              {allPlaceholders.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => onInsertPlaceholder(k)}
                  title="Click to insert into the editor"
                  className="text-xs font-mono px-2 py-1 rounded bg-gray-100 text-gray-700 border border-line hover:bg-gray-200"
                >
                  {k}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </SurfaceCard>
  );
}

SectionsPanel.propTypes = {
  tpl: PropTypes.object,
  onFieldChange: PropTypes.func.isRequired,
  sections: PropTypes.array.isRequired,
  selectedSectionKey: PropTypes.string,
  onSelectSection: PropTypes.func.isRequired,
  onMoveSection: PropTypes.func.isRequired,
  onDeleteSection: PropTypes.func.isRequired,
  onAddSection: PropTypes.func.isRequired,
  unregisteredPlaceholders: PropTypes.array.isRequired,
  onRegisterPlaceholder: PropTypes.func.isRequired,
  allPlaceholders: PropTypes.array.isRequired,
  onInsertPlaceholder: PropTypes.func.isRequired,
  onOpenAddPlaceholder: PropTypes.func.isRequired,
};
